import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Briefcase,
  CalendarDays,
  ChevronRight,
  Database,
  FolderClosed,
  ScrollText,
  Settings as SettingsIcon,
  Users,
} from 'lucide-react';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { cn } from '@/lib/utils';

interface AdminUserListItem {
  id: string;
  fullName: string;
  email: string;
  isActive: boolean;
}

interface PendingAbsence {
  id: string;
}

interface PendingCorrection {
  id: string;
}

export function Admin() {
  // Feature flag del backend (/auth/me). Ausencia = habilitado.
  const { user } = useAuth();
  const documentsEnabled = user?.features?.documents ?? true;
  const usersQuery = useQuery({
    queryKey: ['admin', 'users', 'count'],
    queryFn: () => api<AdminUserListItem[]>('/admin/users'),
  });
  const pendingAbsencesQuery = useQuery({
    queryKey: ['absences', 'pending'],
    queryFn: () => api<PendingAbsence[]>('/absences/pending'),
  });
  const pendingCorrectionsQuery = useQuery({
    queryKey: ['corrections', 'pending'],
    queryFn: () => api<PendingCorrection[]>('/corrections/pending'),
  });

  const totalUsers = usersQuery.data?.length ?? null;
  const activeUsers = usersQuery.data?.filter((u) => u.isActive).length ?? null;
  const pendingAbsences = pendingAbsencesQuery.data?.length ?? null;
  const pendingCorrections = pendingCorrectionsQuery.data?.length ?? null;
  const pendingTotal =
    pendingAbsences != null && pendingCorrections != null
      ? pendingAbsences + pendingCorrections
      : null;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight2">Administración</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Gestión interna de Icaria Atelier.
        </p>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi
          label="Empleados"
          value={activeUsers}
          delta={totalUsers != null && activeUsers != null ? `de ${totalUsers}` : null}
        />
        <Kpi
          label="Solicitudes pendientes"
          value={pendingTotal}
          tone={pendingTotal && pendingTotal > 0 ? 'warning' : undefined}
          delta={
            pendingAbsences != null && pendingCorrections != null
              ? `${pendingAbsences} aus · ${pendingCorrections} corr`
              : null
          }
        />
        <Kpi label="Ausencias pendientes" value={pendingAbsences} />
        <Kpi label="Correcciones pendientes" value={pendingCorrections} />
      </div>

      {/* Atención + Atajos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <NeedsAttentionPanel
          pendingAbsences={pendingAbsences}
          pendingCorrections={pendingCorrections}
        />

        <section className="bg-card border border-border rounded-sm shadow-sm overflow-hidden">
          <header className="px-5 py-3.5 border-b border-border">
            <h2 className="text-base font-semibold tracking-tightish">Atajos</h2>
          </header>
          <ul>
            <AdminLink
              to="/admin/users"
              icon={<Users className="h-4 w-4" />}
              title="Empleados"
              description="Alta, baja, edición, reset de contraseña y manager."
            />
            <AdminLink
              to="/admin/festivos"
              icon={<CalendarDays className="h-4 w-4" />}
              title="Calendario laboral"
              description="Festivos no laborables (alta manual o import por año)."
            />
            <AdminLink
              to="/admin/proyectos"
              icon={<Briefcase className="h-4 w-4" />}
              title="Proyectos"
              description="Catálogo de proyectos para imputar horas al fichar."
            />
            {documentsEnabled ? (
              <AdminLink
                to="/admin/documentos"
                icon={<FolderClosed className="h-4 w-4" />}
                title="Documentos"
                description="Subir y asignar nóminas, contratos y certificados a empleados."
              />
            ) : null}
            <AdminLink
              to="/admin/auditoria"
              icon={<ScrollText className="h-4 w-4" />}
              title="Auditoría"
              description="Registro de acciones sensibles del sistema."
            />
            <AdminLink
              to="/admin/ajustes"
              icon={<SettingsIcon className="h-4 w-4" />}
              title="Ajustes de empresa"
              description="Horario de verano y otras opciones globales."
            />
            <AdminLink
              to="/admin/copia-de-seguridad"
              icon={<Database className="h-4 w-4" />}
              title="Copia de seguridad"
              description="Descargar un ZIP con base de datos y documentos para guardar fuera del servidor."
            />
          </ul>
        </section>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  delta,
  tone,
}: {
  label: string;
  value: number | null;
  delta?: string | null;
  tone?: 'warning';
}) {
  const display = value === null ? '—' : String(value);
  const valueClass = tone === 'warning' && value && value > 0 ? 'text-warning' : 'text-foreground';
  return (
    <div className="bg-card border border-border rounded-sm shadow-sm px-5 py-4">
      <p
        className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
        style={{ letterSpacing: '0.14em' }}
      >
        {label}
      </p>
      <p className={cn('font-mono text-[32px] font-medium tracking-tight2 tabular-nums mt-1', valueClass)}>
        {display}
      </p>
      {delta ? (
        <p className="font-mono text-[12px] text-muted-foreground tabular-nums mt-0.5">{delta}</p>
      ) : null}
    </div>
  );
}

function NeedsAttentionPanel({
  pendingAbsences,
  pendingCorrections,
}: {
  pendingAbsences: number | null;
  pendingCorrections: number | null;
}) {
  const hasAbsences = pendingAbsences != null && pendingAbsences > 0;
  const hasCorrections = pendingCorrections != null && pendingCorrections > 0;
  const empty = !hasAbsences && !hasCorrections;

  return (
    <section className="bg-card border border-border rounded-sm shadow-sm overflow-hidden">
      <header className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold tracking-tightish">Necesita atención</h2>
        {!empty ? (
          <Link
            to="/solicitudes?tab=pending"
            className="text-[12px] font-medium text-icaria-600 hover:text-icaria-700"
          >
            Ver todo →
          </Link>
        ) : null}
      </header>
      {empty ? (
        <p className="px-5 py-8 text-center text-sm text-muted-foreground">
          {pendingAbsences === null
            ? 'Cargando…'
            : 'No hay solicitudes pendientes.'}
        </p>
      ) : (
        <ul>
          {hasAbsences ? (
            <AttentionRow
              tone="amber"
              kind="Ausencias"
              count={pendingAbsences!}
              to="/solicitudes?tab=pending"
            />
          ) : null}
          {hasCorrections ? (
            <AttentionRow
              tone="amber"
              kind="Correcciones"
              count={pendingCorrections!}
              to="/solicitudes?tab=pending"
            />
          ) : null}
        </ul>
      )}
    </section>
  );
}

function AttentionRow({
  tone,
  kind,
  count,
  to,
}: {
  tone: 'amber' | 'red';
  kind: string;
  count: number;
  to: string;
}) {
  const pillTone =
    tone === 'amber'
      ? 'bg-warning/10 border-warning/40 text-warning'
      : 'bg-destructive/10 border-destructive/40 text-destructive';
  return (
    <li className="border-b border-border last:border-b-0">
      <Link
        to={to}
        className="flex items-center gap-3 px-5 py-3.5 hover:bg-paper transition-colors"
      >
        <span
          className={cn(
            'inline-flex items-center font-mono text-[10px] font-medium uppercase border rounded-[3px] px-2 py-0.5',
            pillTone,
          )}
          style={{ letterSpacing: '0.06em' }}
        >
          {kind}
        </span>
        <span className="flex-1 text-[13px] text-foreground">
          <span className="font-mono font-semibold tabular-nums">{count}</span>{' '}
          <span className="text-muted-foreground">
            sin {kind.toLowerCase().endsWith('s') ? 'aprobar' : 'aprobar'}
          </span>
        </span>
        <span className="text-[12px] font-medium text-icaria-600">Revisar →</span>
      </Link>
    </li>
  );
}

function AdminLink({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <li className="border-b border-border last:border-b-0">
      <Link to={to} className="flex items-start gap-3 px-5 py-4 hover:bg-paper transition-colors">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-icaria-100 text-icaria-700 flex-shrink-0">
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-[14px] font-semibold tracking-tightish">{title}</h3>
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </div>
          <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
            {description}
          </p>
        </div>
      </Link>
    </li>
  );
}
